import { useContext, useEffect, useRef } from 'react'
import { notificationsService } from '../utils/signalRNotifications/notifications-service'
import {
	NotificationGroupEntity,
	NotificationMessage,
	SIgnalRNotificationEventType,
} from '../model/signalRNotifications'
import { SignalRContext } from '../providers/SignalRProvider'
import throttle from 'lodash/throttle'
import { serializeSnapshot } from '../utils/screenshare/snapshot'
import { uploadSnapshot } from '../utils/screenshare/snapshot-upload'
import { useEvent } from './useEvent'
import { useDispatch, useSelector } from 'react-redux'
import cursorActions from '../features/cursor/actions'
import { selectExamData } from '../features/exam/selectors'
import { CursorPosition, ZoomData } from '../model/model'

export const SCREENSHARE_THROTTLE = 1000
// A static page produces no DOM mutations, so the snapshot is resent for doctors who join later.
export const SCREENSHARE_KEEPALIVE = 30 * 1000

export const useConnectScreenshare = () => {
	const ignoreNextMutationRef = useRef(false)

	const dispatch = useDispatch()
	const { subscribe } = useEvent()

	const { connectionId } = useContext(SignalRContext)
	const exam = useSelector(selectExamData)

	const examId = exam?._id
	const notificationGroupName = notificationsService.composeGroupName(
		NotificationGroupEntity.Exam,
		examId,
	)

	useEffect(() => {
		if (!examId) return

		let lastHandledHtml: string | undefined
		let lastUploadedAt = 0
		let uploading = false
		let sendAgainWhenDone = false
		let disposed = false

		const send = async (isKeepAlive = false) => {
			if (uploading) {
				sendAgainWhenDone = true
				return
			}
			uploading = true
			try {
				const html = serializeSnapshot()
				const unchanged = html === lastHandledHtml
				const keepAliveDue =
					isKeepAlive && Date.now() - lastUploadedAt >= SCREENSHARE_KEEPALIVE
				// Unchanged too-large HTML is never resent: lastUploadedAt stays behind for it.
				if (unchanged && !(keepAliveDue && lastUploadedAt > 0)) return

				const result = await uploadSnapshot(examId, html)
				if (result === 'uploaded') {
					lastHandledHtml = html
					lastUploadedAt = Date.now()
				} else if (result === 'too-large') {
					lastHandledHtml = html
					lastUploadedAt = 0
				}
			} catch (error) {
				console.error('Screenshare snapshot not sent', error)
			} finally {
				uploading = false
			}

			if (sendAgainWhenDone && !disposed) {
				sendAgainWhenDone = false
				void send()
			}
		}

		const sendThrottled = throttle(() => {
			if (ignoreNextMutationRef.current) {
				ignoreNextMutationRef.current = false
				return
			}
			void send()
		}, SCREENSHARE_THROTTLE)

		const observer = new MutationObserver(sendThrottled)
		observer.observe(document.body, {
			attributes: true,
			childList: true,
			subtree: true,
		})
		const keepAlive = setInterval(() => void send(true), SCREENSHARE_KEEPALIVE)
		sendThrottled()

		return () => {
			disposed = true
			observer.disconnect()
			sendThrottled.cancel()
			clearInterval(keepAlive)
		}
	}, [examId])

	useEffect(() => {
		if (!connectionId || !examId) return

		notificationsService.addConnectionToGroup(notificationGroupName)

		return () => {
			notificationsService.removeConnectionFromGroup(notificationGroupName)
		}
	}, [connectionId, examId, notificationGroupName])

	useEffect(() => {
		const unsubscribeZoom = subscribe(
			SIgnalRNotificationEventType.ZoomShare,
			(event: NotificationMessage) => {
				if (event.type !== SIgnalRNotificationEventType.ZoomShare) return

				const data = event.data as ZoomData | undefined

				ignoreNextMutationRef.current = true

				dispatch(
					cursorActions.setZoomIndex({
						x: data?.positionX ?? 0,
						y: data?.positionY ?? 0,
						zoomIndex: data?.scale ?? 1,
					}),
				)
			},
			{ self: true },
		)

		const unsubscribeCursor = subscribe(
			SIgnalRNotificationEventType.CursorShare,
			(event: NotificationMessage) => {
				if (event.type !== SIgnalRNotificationEventType.CursorShare) return

				const data = event.data as CursorPosition

				dispatch(
					cursorActions.setCursorPosition({
						x: data.x,
						y: data.y,
					}),
				)
				dispatch(cursorActions.showCursor())
			},
			{ self: true },
		)

		return () => {
			unsubscribeCursor()
			unsubscribeZoom()
		}
	}, [])
}
